/**
 * 钱包模块 API 服务
 * 需要登录认证
 */

import type {
  SecurityFields,
  GetBalanceRequest,
  WalletInfo,
  ListTransactionsRequest,
  Transaction,
  CreateDepositRequest,
  CreateDepositResponse,
  CreateWithdrawalRequest,
  CreateWithdrawalResponse,
  WithdrawalOrder,
  ListDepositsRequest,
  ListDepositsData,
  ListWithdrawalsRequest,
  ListWithdrawalsData,
  CurrencyInfo,
  PaginatedResponse,
  GetWithdrawOptionsRequest,
  GetWithdrawOptionsResponse,
  ListBonusOrdersRequest,
  ListBonusOrdersData,
  RecoverBalanceData,
} from '@/types/api'
import { post } from '@/services/request'

/**
 * 获取余额
 */
export async function getBalance(currency: string): Promise<{
  success: boolean
  data?: WalletInfo
  error?: string
}> {
  try {
    const response = await post<Omit<GetBalanceRequest, keyof SecurityFields>, WalletInfo>(
      '/api/v1/wallet/balance',
      { currency },
      { auth: true }
    )

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '获取余额失败' }
  } catch (error) {
    console.error('Get balance error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取交易记录
 */
export async function getTransactions(params: Omit<ListTransactionsRequest, keyof SecurityFields> = {}): Promise<{
  success: boolean
  data?: PaginatedResponse<Transaction>
  error?: string
}> {
  try {
    const response = await post<typeof params, PaginatedResponse<Transaction>>('/api/v1/wallet/transactions', params, {
      auth: true,
    })

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '获取交易记录失败' }
  } catch (error) {
    console.error('Get transactions error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 找回余额 — 把卡在游戏会话里的余额拉回主钱包
 */
export async function recoverBalance(): Promise<{
  success: boolean
  data?: RecoverBalanceData
  error?: string
}> {
  try {
    const response = await post<object, RecoverBalanceData>('/api/v1/wallet/recover', {}, { auth: true })

    if (response.code === 0) {
      return { success: true, data: response.data ?? { session_count: 0, total_recovered: '0' } }
    }

    return { success: false, error: response.msg || '找回余额失败' }
  } catch (error) {
    console.error('Recover balance error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取赠金记录列表
 */
export async function getBonusOrders(params: Omit<ListBonusOrdersRequest, keyof SecurityFields> = {}): Promise<{
  success: boolean
  data?: ListBonusOrdersData
  error?: string
}> {
  try {
    const response = await post<typeof params, ListBonusOrdersData>('/api/v1/wallet/bonus/orders', params, {
      auth: true,
    })

    // code === 0 即成功; 无数据时兜底空列表
    if (response.code === 0) {
      return {
        success: true,
        data: response.data ?? { items: [], pagination: {}, total_amount: '0', total_count: 0 },
      }
    }

    return { success: false, error: response.msg || '获取赠金记录失败' }
  } catch (error) {
    console.error('Get bonus orders error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 创建充值订单
 */
export async function createDeposit(params: Omit<CreateDepositRequest, keyof SecurityFields>): Promise<{
  success: boolean
  data?: CreateDepositResponse
  error?: string
}> {
  try {
    const response = await post<typeof params, CreateDepositResponse>('/api/v1/wallet/deposit', params, { auth: true })

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '创建充值订单失败' }
  } catch (error) {
    console.error('Create deposit error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 创建提现订单
 */
export async function createWithdrawal(params: Omit<CreateWithdrawalRequest, keyof SecurityFields>): Promise<{
  success: boolean
  data?: CreateWithdrawalResponse
  error?: string
}> {
  try {
    const response = await post<typeof params, CreateWithdrawalResponse>('/api/v1/wallet/withdraw', params, { auth: true })

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '创建提现订单失败' }
  } catch (error) {
    console.error('Create withdrawal error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取充值订单列表
 */
export async function getDepositOrders(params: Omit<ListDepositsRequest, keyof SecurityFields> = {}): Promise<{
  success: boolean
  data?: ListDepositsData
  error?: string
}> {
  try {
    const response = await post<typeof params, ListDepositsData>('/api/v1/wallet/deposit/orders', params, {
      auth: true,
    })

    // code === 0 即成功; 无数据时兜底空列表
    if (response.code === 0) {
      return {
        success: true,
        data: response.data ?? { items: [], pagination: {}, total_amount: '0', total_count: 0 },
      }
    }

    return { success: false, error: response.msg || '获取充值订单列表失败' }
  } catch (error) {
    console.error('Get deposit orders error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取提现订单列表
 */
export async function getWithdrawalOrders(params: Omit<ListWithdrawalsRequest, keyof SecurityFields> = {}): Promise<{
  success: boolean
  data?: PaginatedResponse<WithdrawalOrder>
  error?: string
}> {
  try {
    const response = await post<typeof params, PaginatedResponse<WithdrawalOrder>>(
      '/api/v1/wallet/withdraw/orders',
      params,
      { auth: true }
    )

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '获取提现订单列表失败' }
  } catch (error) {
    console.error('Get withdrawal orders error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取提现订单列表 (列表页用, 返回带汇总的完整结构)
 * 注: getWithdrawalOrders 供详情页按 order_no 查找, 字段结构不同, 故单独一个
 */
export async function getWithdrawalOrderList(
  params: Omit<ListWithdrawalsRequest, keyof SecurityFields> = {}
): Promise<{
  success: boolean
  data?: ListWithdrawalsData
  error?: string
}> {
  try {
    const response = await post<typeof params, ListWithdrawalsData>('/api/v1/wallet/withdraw/orders', params, {
      auth: true,
    })

    if (response.code === 0) {
      return {
        success: true,
        data: response.data ?? { items: [], pagination: {}, total_amount: '0', total_count: 0 },
      }
    }

    return { success: false, error: response.msg || '获取提现订单列表失败' }
  } catch (error) {
    console.error('Get withdrawal order list error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取启用币种列表
 */
export async function getCurrencies(): Promise<{
  success: boolean
  data?: CurrencyInfo[]
  error?: string
}> {
  try {
    const response = await post<object, { items: CurrencyInfo[] }>('/api/v1/wallet/currencies', {}, { auth: true })

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data.items }
    }

    return { success: false, error: response.msg || '获取币种列表失败' }
  } catch (error) {
    console.error('Get currencies error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取提现选项（通道 + 绑定银行卡）
 */
export async function getWithdrawOptions(currency: string): Promise<{
  success: boolean
  data?: GetWithdrawOptionsResponse
  error?: string
}> {
  try {
    const response = await post<Omit<GetWithdrawOptionsRequest, keyof SecurityFields>, GetWithdrawOptionsResponse>(
      '/api/v1/wallet/withdraw/options',
      { currency },
      { auth: true }
    )

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '获取提现选项失败' }
  } catch (error) {
    console.error('Get withdraw options error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}
